import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router";
import { API_BASE } from "@/lib/api";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Boxes,
  BrainCircuit,
  Brush,
  Bot,
  CalendarClock,
  CheckCircle2,
  CreditCard,
  ExternalLink,
  FileSearch,
  FileText,
  Filter,
  FlaskConical,
  Gauge,
  GitCompare,
  Home,
  KeyRound,
  Layers3,
  LayoutGrid,
  Link as LinkIcon,
  ListChecks,
  Map,
  MessagesSquare,
  Mic2,
  ReceiptText,
  Rocket,
  Search,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  TableProperties,
  Wand2,
  WalletCards,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

type ServiceStatus = "production" | "beta" | "demo" | "experimental" | "disabled" | string;
type LaunchMode = "internal" | "external" | string;
type CatalogPhaseId = "phase-1" | "phase-2" | "phase-3" | "phase-4";
type SortMode = "recommended" | "name" | "price-asc" | "time-asc";
type HubTab = "web" | "messaging";

type AiService = {
  id: string;
  name: string;
  icon?: string;
  description?: string;
  inputs?: Array<unknown>;
  externalUrl?: string;
  launchMode?: LaunchMode;
  status?: ServiceStatus;
  category?: string;
  tagline?: string;
  executionMode?: string;
  provider?: string;
  estimatedTimeSec?: number;
  maxUploadMb?: number;
  outputLabel?: string;
  accent?: string;
  tags?: string[];
  highlights?: string[];
  useCases?: string[];
  businessValue?: string;
  disclaimers?: string[];
  sortOrder?: number;
  phase?: string | number;
  roadmapPhase?: string | number;
  catalogPhase?: string | number;
  billing?: {
    displayPrice?: string;
    mode?: string;
    unitAmount?: number;
  };
  freeTier?: {
    enabled?: boolean;
    label?: string;
  };
  paidTier?: {
    label?: string;
  };
};

type SuiteProduct = {
  id: string;
  name: string;
  priority: "P0" | "P1" | "P2" | "P3";
  segment: string;
  positioning: string;
  defaultWorkspaceType: string;
  channels: string[];
  pricing: Array<{
    name: string;
    priceUsd: string;
    limits: Record<string, number | string>;
  }>;
  tools: Array<{
    id: string;
    name: string;
    description: string;
    risk: "low" | "medium" | "high";
    disclaimer?: string;
    requiresConsent?: boolean;
    requiresHumanApproval?: boolean;
    serviceIds?: string[];
  }>;
  workflow?: string[];
  useCases?: string[];
  guardrails: string[];
  disclaimers?: string[];
};

type WebProduct = {
  id: string;
  name: string;
  priority: "P0" | "P1" | "P2" | "P3";
  segment: string;
  positioning: string;
  category: string;
  serviceIds: string[];
  modules: Array<{
    id: string;
    name: string;
    serviceIds: string[];
    description: string;
    risk: "low" | "medium" | "high";
    disclaimer?: string;
    requiresPayment?: boolean;
    requiresHumanReview?: boolean;
  }>;
  pricing: Array<{
    name: string;
    priceUsd: string;
    limits: Record<string, number | string>;
  }>;
  guardrails: string[];
  disclaimers?: string[];
  requiredEnv: string[];
  optionalEnv: string[];
};

const PAGE_SIZE = 12;
const ALL = "Todos";

const IconMap: Record<string, LucideIcon> = {
  Activity,
  BarChart3,
  Boxes,
  BrainCircuit,
  Brush,
  FileSearch,
  FileText,
  Gauge,
  GitCompare,
  Home,
  LayoutGrid,
  LinkIcon,
  ListChecks,
  Map,
  MessagesSquare,
  Mic2,
  ReceiptText,
  ShieldCheck,
  ShoppingBag,
  TableProperties,
  Wand2,
  Zap,
};

const statusLabels: Record<string, string> = {
  production: "Producción",
  beta: "Beta vendible",
  demo: "Demo guiada",
  experimental: "Experimental",
};

const launchModeLabels: Record<string, string> = {
  internal: "Internas",
  external: "Miniapps externas",
};

const phaseMeta: Record<CatalogPhaseId, { label: string; shortLabel: string; description: string }> = {
  "phase-1": {
    label: "Fase 1: quick wins",
    shortLabel: "Fase 1",
    description: "Servicios livianos, vendibles y de entrega inmediata.",
  },
  "phase-2": {
    label: "Fase 2: procesamiento pesado",
    shortLabel: "Fase 2",
    description: "Documentos, audio, imagen o datos con mayor carga operativa.",
  },
  "phase-3": {
    label: "Fase 3: compliance",
    shortLabel: "Fase 3",
    description: "Casos sensibles que exigen controles, disclaimers o APIs oficiales.",
  },
  "phase-4": {
    label: "Fase 4: marketplace/API",
    shortLabel: "Fase 4",
    description: "Servicios preparados para consumo recurrente, API o paquetes.",
  },
};

function getAccentClass(accent?: string) {
  const accents: Record<string, string> = {
    amber: "from-amber-300/30 via-orange-400/10 to-transparent text-amber-200 border-amber-300/20",
    cyan: "from-cyan-300/30 via-sky-400/10 to-transparent text-cyan-200 border-cyan-300/20",
    emerald: "from-emerald-300/30 via-teal-400/10 to-transparent text-emerald-200 border-emerald-300/20",
    lime: "from-lime-300/30 via-green-400/10 to-transparent text-lime-200 border-lime-300/20",
    orange: "from-orange-300/30 via-amber-400/10 to-transparent text-orange-200 border-orange-300/20",
    rose: "from-rose-300/30 via-red-400/10 to-transparent text-rose-200 border-rose-300/20",
    violet: "from-violet-300/30 via-fuchsia-400/10 to-transparent text-violet-200 border-violet-300/20",
  };
  return accents[accent || ""] || "from-slate-300/20 via-white/5 to-transparent text-slate-200 border-white/10";
}

export function AiServiceHub() {
  const [activeTab, setActiveTab] = useState<HubTab>("web");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [phase, setPhase] = useState<typeof ALL | CatalogPhaseId>(ALL);
  const [launchMode, setLaunchMode] = useState(ALL);
  const [sortMode, setSortMode] = useState<SortMode>("recommended");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const deferredQuery = useDeferredValue(query);

  const { data: services = [], isLoading, isError } = useQuery<AiService[]>({
    queryKey: ["ai", "services"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/services`);
      if (!res.ok) throw new Error("Failed to load services");
      return res.json();
    },
  });

  const { data: productSuites = [], isLoading: isLoadingProducts, isError: isProductsError } = useQuery<SuiteProduct[]>({
    queryKey: ["suite-products"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/products/products`);
      if (!res.ok) throw new Error("Failed to load product suites");
      const payload = await res.json();
      return payload.products || [];
    },
  });

  const { data: webProducts = [], isLoading: isLoadingWebProducts, isError: isWebProductsError } = useQuery<WebProduct[]>({
    queryKey: ["web-products"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/web-products/products`);
      if (!res.ok) throw new Error("Failed to load web products");
      const payload = await res.json();
      return payload.products || [];
    },
  });

  const categories = useMemo(() => [ALL, ...uniqueSorted(services.map((service) => service.category))], [services]);
  const statuses = useMemo(() => [ALL, ...uniqueSorted(services.map((service) => service.status))], [services]);
  const launchModes = useMemo(() => [ALL, ...uniqueSorted(services.map((service) => service.launchMode || "internal"))], [services]);
  const availablePhases = useMemo(() => {
    const phases = new Set<CatalogPhaseId>(services.map(getServicePhase));
    return [ALL, ...Object.keys(phaseMeta).filter((item): item is CatalogPhaseId => phases.has(item as CatalogPhaseId))];
  }, [services]);

  const filteredServices = useMemo(() => {
    const term = deferredQuery.trim().toLowerCase();
    return services
      .filter((service) => {
        const servicePhase = getServicePhase(service);
        const serviceLaunchMode = service.launchMode || "internal";
        const matchesCategory = category === ALL || service.category === category;
        const matchesStatus = status === ALL || service.status === status;
        const matchesPhase = phase === ALL || servicePhase === phase;
        const matchesLaunchMode = launchMode === ALL || serviceLaunchMode === launchMode;
        const text = [
          service.name,
          service.description,
          service.tagline,
          service.category,
          service.provider,
          service.outputLabel,
          service.businessValue,
          ...(service.tags || []),
          ...(service.highlights || []),
          ...(service.useCases || []),
        ]
          .join(" ")
          .toLowerCase();
        return matchesCategory && matchesStatus && matchesPhase && matchesLaunchMode && (!term || text.includes(term));
      })
      .sort((a, b) => sortServices(a, b, sortMode));
  }, [services, deferredQuery, category, status, phase, launchMode, sortMode]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [deferredQuery, category, status, phase, launchMode, sortMode]);

  const visibleServices = filteredServices.slice(0, visibleCount);
  const productionCount = services.filter((service) => service.status === "production").length;
  const groupedServiceCount = new Set(webProducts.flatMap((product) => product.serviceIds)).size;
  const sellableProductCount =
    productSuites.filter((product) => product.priority === "P0" || product.priority === "P1").length
    + webProducts.filter((product) => product.priority === "P0" || product.priority === "P1").length;
  const hasActiveFilters = [category, status, phase, launchMode].some((value) => value !== ALL) || Boolean(query.trim());

  return (
    <div className="space-y-8 pb-16">
      <section className="hero-panel overflow-hidden rounded-[2rem] border border-white/10 p-6 shadow-2xl sm:p-8 lg:p-10">
        <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-300/10 px-4 py-2 text-sm text-cyan-100">
              <Sparkles className="h-4 w-4" />
              Alquim-IA Demo Suite
            </div>
            <div className="space-y-4">
              <h1 className="max-w-5xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
                Catálogo de servicios IA listo para crecer sin perder foco comercial.
              </h1>
              <p className="max-w-3xl text-base leading-7 text-slate-300 sm:text-lg">
                Showroom operativo para quick wins, procesamiento pesado y servicios sensibles de fase 3. Busca por problema,
                filtra por madurez y abre cada demo con contexto de precio, entrega y valor.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/ai-suite/landings/messaging/tiendita-bot-os">
                <Button className="h-12 rounded-full bg-cyan-200 px-6 font-bold text-slate-950 hover:bg-white">
                  Ver landing estrella
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href="#suite-catalog">
                <Button
                  variant="outline"
                  className="h-12 rounded-full border-white/15 bg-white/5 px-6 text-white hover:bg-white/10"
                  onClick={() => setActiveTab("web")}
                >
                  Explorar suite
                </Button>
              </a>
              <a href="#whatsapp-products">
                <Button
                  variant="outline"
                  className="h-12 rounded-full border-emerald-200/20 bg-emerald-300/10 px-6 text-emerald-50 hover:bg-emerald-300/20"
                  onClick={() => setActiveTab("messaging")}
                >
                  Ver productos WhatsApp
                  <MessagesSquare className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Metric label="Servicios activos" value={services.length} />
            <Metric label="Demos producción" value={productionCount} />
            <Metric label="Productos Web" value={isLoadingWebProducts ? "..." : webProducts.length} />
            <Metric label="Productos WhatsApp" value={isLoadingProducts ? "..." : productSuites.length} />
            <Metric label="P0/P1 vendibles" value={isLoadingProducts || isLoadingWebProducts ? "..." : sellableProductCount} />
          </div>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-white/[0.045] p-2 shadow-xl">
        <div className="grid gap-2 md:grid-cols-2">
          <TabButton
            active={activeTab === "web"}
            icon={LayoutGrid}
            title="Servicios Web / Miniapps"
            description={`${webProducts.length || 12} productos integrados sobre ${groupedServiceCount || services.length} servicios y workers.`}
            onClick={() => setActiveTab("web")}
          />
          <TabButton
            active={activeTab === "messaging"}
            icon={MessagesSquare}
            title="WhatsApp / Telegram"
            description={`${productSuites.length || 10} productos conversacionales con workspaces, contactos y webhooks.`}
            onClick={() => setActiveTab("messaging")}
          />
        </div>
      </section>

      {activeTab === "web" && (
        <>
          {isWebProductsError ? <CatalogError title="Productos web no disponibles" body="La API de productos web no respondió correctamente. Revisa /api/web-products/health antes de vender." /> : null}
          <WebProductSuites products={webProducts} services={services} isLoading={isLoadingWebProducts} />

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {(Object.keys(phaseMeta) as CatalogPhaseId[]).slice(0, 4).map((item) => {
              const count = services.filter((service) => getServicePhase(service) === item).length;
              return (
                <button
                  key={item}
                  type="button"
                  onClick={() => setPhase((current) => (current === item ? ALL : item))}
                  className={`rounded-3xl border p-5 text-left transition hover:-translate-y-0.5 ${
                    phase === item ? "border-cyan-200/40 bg-cyan-200/10" : "border-white/10 bg-white/[0.045] hover:bg-white/[0.07]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-black text-white">{phaseMeta[item].shortLabel}</span>
                    <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-slate-300">{count}</span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-400">{phaseMeta[item].description}</p>
                </button>
              );
            })}
          </section>

          <section id="suite-catalog" className="space-y-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h2 className="text-2xl font-black">Servicios Web / Miniapps</h2>
            <p className="text-sm text-slate-400">
              Catálogo granular: {filteredServices.length} de {services.length} workers/servicios visibles con los filtros actuales.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[minmax(18rem,22rem)_repeat(4,11rem)]">
            <label className="relative block sm:col-span-2 lg:col-span-3 xl:col-span-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por servicio, caso de uso, vertical..."
                className="h-12 w-full rounded-full border border-white/10 bg-white/5 pl-11 pr-4 text-sm outline-none transition focus:border-cyan-200/40"
              />
            </label>
            <FilterSelect icon={Filter} label="Categoría" value={category} onChange={setCategory} options={categories} />
            <FilterSelect
              icon={Rocket}
              label="Estado"
              value={status}
              onChange={setStatus}
              options={statuses}
              formatLabel={(value) => (value === ALL ? value : statusLabels[value] || value)}
            />
            <FilterSelect
              icon={Layers3}
              label="Fase"
              value={phase}
              onChange={(value) => setPhase(value as typeof ALL | CatalogPhaseId)}
              options={availablePhases}
              formatLabel={(value) => (value === ALL ? value : phaseMeta[value as CatalogPhaseId]?.shortLabel || value)}
            />
            <FilterSelect
              icon={SlidersHorizontal}
              label="Orden"
              value={sortMode}
              onChange={(value) => setSortMode(value as SortMode)}
              options={["recommended", "name", "price-asc", "time-asc"]}
              formatLabel={(value) => {
                const labels: Record<string, string> = {
                  recommended: "Recomendado",
                  name: "Nombre",
                  "price-asc": "Precio",
                  "time-asc": "Tiempo",
                };
                return labels[value] || value;
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {launchModes.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setLaunchMode(item)}
              className={`rounded-full border px-4 py-2 text-sm transition ${
                launchMode === item ? "border-cyan-200/40 bg-cyan-200/10 text-cyan-50" : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
              }`}
            >
              {item === ALL ? "Todos los modos" : launchModeLabels[item] || item}
            </button>
          ))}
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setQuery("");
                setCategory(ALL);
                setStatus(ALL);
                setPhase(ALL);
                setLaunchMode(ALL);
              }}
              className="rounded-full border border-rose-200/20 bg-rose-400/10 px-4 py-2 text-sm text-rose-100 hover:bg-rose-400/20"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 9 }, (_, index) => (
              <Skeleton key={index} className="h-96 rounded-3xl bg-white/10" />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-3xl border border-red-300/20 bg-red-500/10 p-6 text-red-100">
            No se pudo cargar el catálogo. Revisa el backend de la AI Suite.
          </div>
        ) : filteredServices.length === 0 ? (
          <EmptyCatalog onReset={() => {
            setQuery("");
            setCategory(ALL);
            setStatus(ALL);
            setPhase(ALL);
            setLaunchMode(ALL);
          }} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {visibleServices.map((service) => (
                <ServiceCard key={service.id} service={service} />
              ))}
            </div>
            {visibleCount < filteredServices.length && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  className="h-12 rounded-full border-white/15 bg-white/5 px-6 text-white hover:bg-white/10"
                  onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
                >
                  Cargar {Math.min(PAGE_SIZE, filteredServices.length - visibleCount)} más
                </Button>
              </div>
            )}
          </>
        )}
          </section>
        </>
      )}

      {activeTab === "messaging" && (
        <>
          {isProductsError ? <CatalogError title="Productos WhatsApp/Telegram no disponibles" body="La API conversacional no respondió correctamente. Revisa /api/products/health, webhooks y secretos antes de vender." /> : null}
          <WhatsAppProductSuites products={productSuites} isLoading={isLoadingProducts} />
        </>
      )}
    </div>
  );
}

function TabButton({
  active,
  icon: Icon,
  title,
  description,
  onClick,
}: {
  active: boolean;
  icon: LucideIcon;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-[1.65rem] border p-5 text-left transition ${
        active
          ? "border-cyan-200/35 bg-cyan-200/10 shadow-[0_0_40px_rgba(34,211,238,0.10)]"
          : "border-white/10 bg-black/15 hover:bg-white/[0.06]"
      }`}
    >
      <div className="flex items-center gap-3">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${active ? "bg-cyan-200 text-slate-950" : "bg-white/10 text-slate-200"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-lg font-black text-white">{title}</h2>
          <p className="mt-1 text-sm leading-5 text-slate-400">{description}</p>
        </div>
      </div>
    </button>
  );
}

function Metric({ label, value, suffix }: { label: string; value: string | number; suffix?: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur">
      <div className="text-3xl font-black text-white">
        {value}
        {suffix && <span className="ml-1 text-sm text-cyan-200">{suffix}</span>}
      </div>
      <div className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-400">{label}</div>
    </div>
  );
}

function WebProductSuites({ products, services, isLoading }: { products: WebProduct[]; services: AiService[]; isLoading: boolean }) {
  const sellableProducts = products.filter((product) => product.priority === "P0" || product.priority === "P1").length;
  const serviceCount = new Set(products.flatMap((product) => product.serviceIds)).size;
  const moduleCount = products.reduce((sum, product) => sum + product.modules.length, 0);

  return (
    <section className="space-y-5 rounded-[2rem] border border-cyan-200/15 bg-cyan-300/[0.035] p-5 shadow-2xl sm:p-6 lg:p-7">
      <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr] lg:items-start">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200/20 bg-cyan-300/10 px-4 py-2 text-sm text-cyan-100">
            <LayoutGrid className="h-4 w-4" />
            Productos Web / Miniapps
          </div>
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl">Productos integrados, no una lista infinita de tools.</h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">
              Esta capa empaqueta los servicios web existentes en productos vendibles con workspace, checkout product-level,
              idempotencia, rate/fraud guard, auditoría y rutas de ejecución hacia los workers actuales.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label="Productos" value={isLoading ? "..." : products.length} />
            <Metric label="Servicios agrupados" value={isLoading ? "..." : serviceCount} />
            <Metric label="P0/P1 vendibles" value={isLoading ? "..." : sellableProducts} />
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-black/25 p-5">
          <div className="flex items-center gap-2 text-sm font-black text-white">
            <KeyRound className="h-4 w-4 text-amber-200" />
            Backend de producto web
          </div>
          <p className="mt-2 text-sm leading-6 text-slate-300">Ruta del servidor Oracle donde se agregan claves y controles:</p>
          <CodeLine value="/home/ubuntu/alquim-ia-ai-suite/.env" />
          <div className="mt-4 grid gap-2">
            {[
              "WEB_PRODUCTS_API_KEY",
              "WEB_PRODUCTS_DATA_DIR=/data/web-products",
              "WEB_PRODUCTS_ENFORCE_PAYMENTS=false|true",
              "WEB_PRODUCTS_RATE_LIMIT=60",
              "STRIPE_SECRET_KEY",
              "XAI_API_KEY",
              "REMOVE_BG_API_KEY / REPLICATE_API_TOKEN / GOOGLE_PLACES_API_KEY segun modulo",
            ].map((item) => (
              <CodeLine key={item} value={item} />
            ))}
          </div>
          <div className="mt-4 rounded-2xl border border-cyan-200/15 bg-cyan-300/10 p-4 text-sm leading-6 text-cyan-50">
            Endpoints:
            <CodeLine value="/api/web-products/products" />
            <CodeLine value="/api/web-products/workspaces/:workspaceId/runs" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {isLoading
          ? Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-96 rounded-3xl bg-white/10" />)
          : products.length > 0
            ? products.map((product) => <WebProductCard key={product.id} product={product} services={services} />)
            : <CatalogEmpty
                title="No hay productos web cargados"
                body="La pestaña web necesita /api/web-products/products con productos reales antes de vender miniapps. El catálogo granular de servicios sigue disponible abajo."
              />}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <OperationalNote
          icon={CreditCard}
          title="Pagos y fraude"
          body="Checkout por producto, runToken, idempotency-key, input hash, rate limit por workspace y auditoría de runs antes de ejecutar módulos pagos."
        />
        <OperationalNote
          icon={Layers3}
          title="Workers existentes"
          body={`Los productos agrupan ${moduleCount} módulos y conservan los endpoints internos /api/services/:id/preview, checkout y execute.`}
        />
        <OperationalNote
          icon={ShieldCheck}
          title="Compliance"
          body="Los módulos legales, médicos, screening, datos externos y automatización sensible quedan marcados con revisión humana o controles de API oficial."
        />
      </div>
    </section>
  );
}

function WebProductCard({ product, services }: { product: WebProduct; services: AiService[] }) {
  const visibleModules = product.modules.slice(0, 6);
  const hiddenModules = Math.max(product.modules.length - visibleModules.length, 0);
  const primaryPrice = product.pricing[0]?.priceUsd || "0";
  const priorityClass =
    product.priority === "P0"
      ? "border-cyan-200/30 bg-cyan-300/10 text-cyan-100"
      : product.priority === "P1"
        ? "border-emerald-200/25 bg-emerald-300/10 text-emerald-100"
        : "border-white/10 bg-white/5 text-slate-200";
  const serviceNames = product.serviceIds
    .slice(0, 4)
    .map((serviceId) => services.find((service) => service.id === serviceId)?.name || serviceId);

  return (
    <Card className="service-card h-full overflow-hidden rounded-[1.5rem] border-white/10 bg-white/[0.045] shadow-xl transition hover:-translate-y-1 hover:border-cyan-200/30 hover:bg-white/[0.07]">
      <CardContent className="flex h-full flex-col gap-5 p-5">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-cyan-300/18 via-emerald-300/8 to-transparent p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black/25 text-cyan-100">
              {getWebProductIcon(product.category)}
            </div>
            <span className={`rounded-full border px-3 py-1 text-xs font-black ${priorityClass}`}>{product.priority}</span>
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.22em] text-white/50">{product.category}</p>
          <h3 className="mt-2 text-2xl font-black text-white">{product.name}</h3>
          <p className="mt-2 text-sm leading-6 text-slate-200">{product.segment}</p>
        </div>

        <p className="text-sm leading-6 text-slate-300">{product.positioning}</p>

        <div className="grid grid-cols-3 gap-2">
          <MiniFact icon={WalletCards} label="Desde" value={primaryPrice === "0" ? "Free" : `USD ${primaryPrice}`} />
          <MiniFact icon={Layers3} label="Módulos" value={String(product.modules.length)} />
          <MiniFact icon={ListChecks} label="Servicios" value={String(product.serviceIds.length)} />
        </div>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Módulos integrados</p>
          <div className="flex flex-wrap gap-2">
            {visibleModules.map((item) => (
              <span key={item.id} className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-slate-300">
                {item.name}
              </span>
            ))}
            {hiddenModules > 0 && <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-slate-400">+{hiddenModules}</span>}
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Workers debajo</p>
          <p className="mt-2 text-sm leading-6 text-slate-300">{serviceNames.join(", ")}{product.serviceIds.length > 4 ? ` +${product.serviceIds.length - 4}` : ""}</p>
        </div>

        <div className="mt-auto rounded-2xl border border-cyan-200/15 bg-cyan-300/10 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-100/70">Flujo de cobro</p>
          <p className="mt-2 text-sm leading-6 text-cyan-50">Entra al producto, elige módulo, crea checkout/runToken y ejecuta contra workers reales con idempotencia y auditoría.</p>
        </div>
        {product.disclaimers?.length ? (
          <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5" />
              Disclaimer
            </div>
            <p className="mt-2 text-xs leading-5 text-amber-50/90">{product.disclaimers[0]}</p>
          </div>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-2">
          <Button asChild variant="outline" className="h-11 rounded-2xl border-cyan-200/20 bg-cyan-300/10 text-cyan-50 hover:bg-cyan-300/20">
            <Link to={`/ai-suite/landings/web/${product.id}`}>
              Landing de pauta
            </Link>
          </Button>
          <Button asChild className="h-11 rounded-2xl bg-white text-slate-950 hover:bg-cyan-100">
            <Link to={`/ai-suite/products/web/${product.id}`}>
              Usar producto
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function getWebProductIcon(category: string) {
  if (category === "documents") return <FileSearch className="h-6 w-6" />;
  if (category === "commerce") return <ShoppingBag className="h-6 w-6" />;
  if (category === "media") return <Brush className="h-6 w-6" />;
  if (category === "audio") return <Mic2 className="h-6 w-6" />;
  if (category === "marketing") return <BarChart3 className="h-6 w-6" />;
  if (category === "text") return <BrainCircuit className="h-6 w-6" />;
  if (category === "legal") return <ShieldCheck className="h-6 w-6" />;
  if (category === "automation") return <GitCompare className="h-6 w-6" />;
  if (category === "medical") return <Activity className="h-6 w-6" />;
  if (category === "real-estate") return <Home className="h-6 w-6" />;
  return <Sparkles className="h-6 w-6" />;
}

function WhatsAppProductSuites({ products, isLoading }: { products: SuiteProduct[]; isLoading: boolean }) {
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const sellableProducts = products.filter((product) => product.priority === "P0" || product.priority === "P1").length;
  const toolCount = products.reduce((sum, product) => sum + product.tools.length, 0);
  const highRiskCount = products.reduce((sum, product) => sum + product.tools.filter((tool) => tool.risk === "high").length, 0);
  const selectedProduct = products.find((product) => product.id === selectedProductId) || products[0];

  return (
    <section id="whatsapp-products" className="space-y-5 rounded-[2rem] border border-emerald-200/15 bg-emerald-300/[0.035] p-5 shadow-2xl sm:p-6 lg:p-7">
      <div className="grid gap-5 lg:grid-cols-[1fr_0.9fr] lg:items-start">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/20 bg-emerald-300/10 px-4 py-2 text-sm text-emerald-100">
            <Bot className="h-4 w-4" />
            Productos WhatsApp / Telegram
          </div>
          <div>
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl">Suites vendibles, no bots sueltos.</h2>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300 sm:text-base">
              Esta capa agrupa los workers de la AI Suite en productos completos con workspace, contactos, autorizaciones,
              threads, mensajes, schedules, tool runs, webhooks y pagos preparados. Solo falta conectar el número de WhatsApp
              o Telegram del producto que se quiera probar primero.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Metric label="Productos" value={isLoading ? "..." : products.length} />
            <Metric label="Workers agrupados" value={isLoading ? "..." : toolCount} />
            <Metric label="P0/P1 vendibles" value={isLoading ? "..." : sellableProducts} />
          </div>
        </div>

        <div className="rounded-3xl border border-white/10 bg-black/25 p-5">
          <div className="flex items-center gap-2 text-sm font-black text-white">
            <KeyRound className="h-4 w-4 text-amber-200" />
            Configuración para producción
          </div>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Ruta del servidor Oracle donde se agregan las claves:
          </p>
          <CodeLine value="/home/ubuntu/alquim-ia-ai-suite/.env" />
          <div className="mt-4 grid gap-2">
            {[
              "SUITE_PRODUCTS_API_KEY",
              "WHATSAPP_VERIFY_TOKEN",
              "WHATSAPP_ACCESS_TOKEN",
              "WHATSAPP_PHONE_NUMBER_ID",
              "TELEGRAM_BOT_TOKEN",
              "SUITE_PRODUCTS_DATA_DIR=/data/suite-products",
            ].map((item) => (
              <CodeLine key={item} value={item} />
            ))}
          </div>
          <div className="mt-4 rounded-2xl border border-cyan-200/15 bg-cyan-300/10 p-4 text-sm leading-6 text-cyan-50">
            Webhook Meta por workspace:
            <CodeLine value="https://tech.alquim-ia.cloud/oracle/api/products/webhooks/whatsapp/<workspaceId>" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {isLoading
          ? Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-80 rounded-3xl bg-white/10" />)
          : products.length > 0
            ? products.map((product) => (
              <ProductSuiteCard
                key={product.id}
                product={product}
                isSelected={selectedProduct?.id === product.id}
                onSelect={() => setSelectedProductId((current) => (current === product.id ? null : product.id))}
              />
            ))
            : <CatalogEmpty
                title="No hay productos WhatsApp/Telegram cargados"
                body="La API conversacional no devolvió suites. Sin productos no hay workspace, checkout ni webhooks listos para vender."
              />}
      </div>

      {!isLoading && selectedProduct && <ProductSuiteDetail product={selectedProduct} />}

      <div className="grid gap-4 lg:grid-cols-3">
        <OperationalNote
          icon={MessagesSquare}
          title="Canales listos"
          body="WhatsApp Cloud API y Telegram webhooks quedan montados por workspace. El envío real de WhatsApp se activa cuando existan token y phone number id."
        />
        <OperationalNote
          icon={ShieldCheck}
          title="Autorizaciones"
          body={`Consentimiento y human approval están modelados. Hay ${highRiskCount} tools marcadas como riesgo alto para legal, salud, screening o mensajes sensibles.`}
        />
        <OperationalNote
          icon={CalendarClock}
          title="Scheduler"
          body="Los productos pueden crear recordatorios, seguimientos, broadcasts programados y tareas recurrentes con persistencia en volumen Docker."
        />
      </div>
    </section>
  );
}

function ProductSuiteCard({ product, isSelected, onSelect }: { product: SuiteProduct; isSelected: boolean; onSelect: () => void }) {
  const primaryPrice = product.pricing[0]?.priceUsd || "0";
  const visibleTools = product.tools.slice(0, 6);
  const hiddenTools = Math.max(product.tools.length - visibleTools.length, 0);
  const priorityClass =
    product.priority === "P0"
      ? "border-emerald-200/30 bg-emerald-300/10 text-emerald-100"
      : product.priority === "P1"
        ? "border-cyan-200/25 bg-cyan-300/10 text-cyan-100"
        : "border-white/10 bg-white/5 text-slate-200";

  return (
    <div className="block h-full text-left">
    <Card className={`service-card h-full overflow-hidden rounded-[1.5rem] bg-white/[0.045] shadow-xl transition hover:-translate-y-1 hover:bg-white/[0.07] ${
      isSelected ? "border-emerald-200/45 ring-2 ring-emerald-200/20" : "border-white/10"
    }`}>
      <CardContent className="flex h-full flex-col gap-5 p-5">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-300/18 via-cyan-300/8 to-transparent p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black/25 text-emerald-100">
              {getProductIcon(product.id)}
            </div>
            <span className={`rounded-full border px-3 py-1 text-xs font-black ${priorityClass}`}>{product.priority}</span>
          </div>
          <p className="mt-4 text-xs uppercase tracking-[0.22em] text-white/50">{product.defaultWorkspaceType} workspace</p>
          <h3 className="mt-2 text-2xl font-black text-white">{product.name}</h3>
          <p className="mt-2 text-sm leading-6 text-slate-200">{product.segment}</p>
        </div>

        <p className="text-sm leading-6 text-slate-300">{product.positioning}</p>

        <div className="grid grid-cols-3 gap-2">
          <MiniFact icon={WalletCards} label="Desde" value={primaryPrice === "0" ? "Free" : `$${primaryPrice}`} />
          <MiniFact icon={MessagesSquare} label="Canales" value={product.channels.join("/")} />
          <MiniFact icon={ListChecks} label="Tools" value={String(product.tools.length)} />
        </div>

        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Qué hace</p>
          <div className="flex flex-wrap gap-2">
            {visibleTools.map((tool) => (
              <span key={tool.id} className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-slate-300">
                {tool.name}
              </span>
            ))}
            {hiddenTools > 0 && <span className="rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-slate-400">+{hiddenTools}</span>}
          </div>
        </div>

        <div className="mt-auto rounded-2xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Modelo comercial y activación</p>
          <div className="mt-2 space-y-1">
            {product.pricing.slice(0, 3).map((tier) => (
              <div key={tier.name} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-slate-300">{tier.name}</span>
                <span className="font-bold text-white">{tier.priceUsd === "0" ? "Free" : `USD ${tier.priceUsd}`}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs leading-5 text-slate-400">Checkout crea workspace pago; demo pública puede estar bloqueada en producción.</p>
        </div>
        {product.disclaimers?.length ? (
          <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5" />
              Disclaimer
            </div>
            <p className="mt-2 text-xs leading-5 text-amber-50/90">{product.disclaimers[0]}</p>
          </div>
        ) : null}
        <div className="grid gap-2 sm:grid-cols-3">
          <Button
            type="button"
            variant="outline"
            onClick={onSelect}
            className="h-11 rounded-2xl border-emerald-200/20 bg-emerald-300/10 text-emerald-50 hover:bg-emerald-300/20"
          >
            {isSelected ? "Viendo detalle" : "Ver detalle"}
          </Button>
          <Button asChild variant="outline" className="h-11 rounded-2xl border-cyan-200/20 bg-cyan-300/10 text-cyan-50 hover:bg-cyan-300/20">
            <Link to={`/ai-suite/landings/messaging/${product.id}`}>
              Landing
            </Link>
          </Button>
          <Button asChild className="h-11 rounded-2xl bg-white text-slate-950 hover:bg-emerald-100">
            <Link to={`/ai-suite/products/messaging/${product.id}`}>
              Usar producto
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
    </div>
  );
}

function ProductSuiteDetail({ product }: { product: SuiteProduct }) {
  return (
    <section className="rounded-[2rem] border border-emerald-200/20 bg-black/25 p-5 shadow-xl sm:p-6">
      <div className="grid gap-5 lg:grid-cols-[0.85fr_1.15fr]">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/20 bg-emerald-300/10 px-4 py-2 text-sm text-emerald-100">
            {getProductIcon(product.id)}
            Detalle vendible
          </div>
          <div>
            <h3 className="text-3xl font-black text-white">{product.name}</h3>
            <p className="mt-3 text-sm leading-7 text-slate-300">{product.positioning}</p>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Para quién sirve</p>
            <p className="mt-2 text-sm leading-6 text-slate-200">{product.segment}</p>
          </div>
          <div className="grid gap-2">
            {(product.useCases || []).map((item) => (
              <div key={item} className="flex items-start gap-2 rounded-2xl border border-white/10 bg-white/[0.04] p-3 text-sm text-slate-300">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-200" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          {product.disclaimers?.length ? (
            <div className="rounded-3xl border border-amber-200/20 bg-amber-300/10 p-5">
              <div className="flex items-center gap-2 text-sm font-black text-amber-50">
                <AlertTriangle className="h-4 w-4" />
                Disclaimer de uso responsable
              </div>
              <div className="mt-3 space-y-2">
                {product.disclaimers.map((item) => (
                  <p key={item} className="text-sm leading-6 text-amber-50/90">- {item}</p>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-5">
          <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Flujo claro de operación</p>
            <div className="mt-4 space-y-3">
              {(product.workflow || []).map((step, index) => (
                <div key={step} className="grid grid-cols-[2.25rem_1fr] gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-200 font-black text-slate-950">{index + 1}</div>
                  <p className="pt-1.5 text-sm leading-6 text-slate-200">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/[0.045] p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Herramientas incluidas</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {product.tools.map((tool) => (
                <div key={tool.id} className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h4 className="font-black text-white">{tool.name}</h4>
                    <span className={`rounded-full border px-2 py-1 text-[0.65rem] uppercase ${
                      tool.risk === "high" ? "border-rose-200/25 bg-rose-400/10 text-rose-100" : tool.risk === "medium" ? "border-amber-200/25 bg-amber-400/10 text-amber-100" : "border-emerald-200/25 bg-emerald-400/10 text-emerald-100"
                    }`}>
                      {tool.risk}
                    </span>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{tool.description}</p>
                  {tool.disclaimer ? (
                    <p className="mt-3 rounded-xl border border-amber-200/15 bg-amber-300/10 p-3 text-xs leading-5 text-amber-50/90">
                      {tool.disclaimer}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {tool.requiresConsent && <span className="rounded-full border border-cyan-200/20 bg-cyan-300/10 px-2 py-1 text-xs text-cyan-100">requiere consentimiento</span>}
                    {tool.requiresHumanApproval && <span className="rounded-full border border-amber-200/20 bg-amber-300/10 px-2 py-1 text-xs text-amber-100">requiere aprobación</span>}
                    {(tool.serviceIds || []).map((serviceId) => (
                      <Link
                        key={serviceId}
                        to={`/ai-suite/${serviceId}`}
                        className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs text-slate-300 hover:border-emerald-200/30 hover:text-emerald-100"
                      >
                        {serviceId}
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-3xl border border-white/10 bg-black/20 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Pricing</p>
              <div className="mt-3 space-y-2">
                {product.pricing.map((tier) => (
                  <div key={tier.name} className="flex items-center justify-between gap-3 rounded-2xl bg-white/[0.04] px-3 py-2 text-sm">
                    <span className="text-slate-300">{tier.name}</span>
                    <span className="font-black text-white">{tier.priceUsd === "0" ? "Free" : `USD ${tier.priceUsd}`}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-white/10 bg-black/20 p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Guardrails</p>
              <div className="mt-3 space-y-2">
                {product.guardrails.slice(0, 4).map((guardrail) => (
                  <p key={guardrail} className="text-sm leading-6 text-slate-300">- {guardrail}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function getProductIcon(productId: string) {
  if (productId.includes("tiendita")) return <ShoppingBag className="h-6 w-6" />;
  if (productId.includes("arriendo")) return <Home className="h-6 w-6" />;
  if (productId.includes("legal")) return <ShieldCheck className="h-6 w-6" />;
  if (productId.includes("freelancer")) return <BriefcaseIcon />;
  if (productId.includes("agenda")) return <CalendarClock className="h-6 w-6" />;
  if (productId.includes("mobility")) return <Map className="h-6 w-6" />;
  if (productId.includes("learning")) return <BrainCircuit className="h-6 w-6" />;
  if (productId.includes("wellness")) return <Activity className="h-6 w-6" />;
  if (productId.includes("family")) return <Home className="h-6 w-6" />;
  return <Sparkles className="h-6 w-6" />;
}

function BriefcaseIcon() {
  return <FileText className="h-6 w-6" />;
}

function OperationalNote({ icon: Icon, title, body }: { icon: LucideIcon; title: string; body: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-black/20 p-5">
      <div className="flex items-center gap-2 text-sm font-black text-white">
        <Icon className="h-4 w-4 text-emerald-200" />
        {title}
      </div>
      <p className="mt-2 text-sm leading-6 text-slate-300">{body}</p>
    </div>
  );
}

function CodeLine({ value }: { value: string }) {
  return (
    <code className="mt-2 block overflow-x-auto rounded-2xl border border-white/10 bg-slate-950/80 px-3 py-2 text-xs text-cyan-100">
      {value}
    </code>
  );
}

function CatalogError({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[1.5rem] border border-rose-200/25 bg-rose-500/10 p-5 text-rose-50">
      <div className="flex items-center gap-2 text-sm font-black">
        <ShieldCheck className="h-4 w-4" />
        {title}
      </div>
      <p className="mt-2 text-sm leading-6 text-rose-100/85">{body}</p>
    </div>
  );
}

function CatalogEmpty({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-[1.5rem] border border-amber-200/25 bg-amber-500/10 p-5 text-amber-50 md:col-span-2 xl:col-span-3">
      <div className="flex items-center gap-2 text-sm font-black">
        <ShieldCheck className="h-4 w-4" />
        {title}
      </div>
      <p className="mt-2 text-sm leading-6 text-amber-100/85">{body}</p>
    </div>
  );
}

function FilterSelect({
  icon: Icon,
  label,
  value,
  onChange,
  options,
  formatLabel = (item) => item,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  formatLabel?: (value: string) => string;
}) {
  return (
    <label className="relative block">
      <span className="sr-only">{label}</span>
      <Icon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-12 w-full appearance-none rounded-full border border-white/10 bg-slate-950/80 pl-11 pr-10 text-sm outline-none transition focus:border-cyan-200/40"
      >
        {options.map((item) => (
          <option key={item} value={item}>
            {formatLabel(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

function ServiceCard({ service }: { service: AiService }) {
  const Icon = IconMap[service.icon || ""] || BrainCircuit;
  const accentClass = getAccentClass(service.accent);
  const isExternal = service.launchMode === "external" && service.externalUrl;
  const cta = isExternal ? "Ver checkout y demo" : "Configurar demo";
  const phase = getServicePhase(service);
  const hiddenTags = Math.max((service.tags?.length || 0) - 3, 0);
  const disclaimers = serviceDisclaimers(service);

  return (
    <Card className="service-card group overflow-hidden rounded-[1.5rem] border-white/10 bg-white/[0.045] shadow-xl transition duration-300 hover:-translate-y-1 hover:border-cyan-200/30 hover:bg-white/[0.07]">
      <CardContent className="flex h-full flex-col gap-5 p-6">
        <div className={`rounded-3xl border bg-gradient-to-br p-4 ${accentClass}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-black/25">
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="rounded-full border border-white/10 bg-black/25 px-3 py-1 text-xs font-semibold text-white/80">
                {statusLabels[service.status || ""] || service.status || "Demo"}
              </span>
              <span className="rounded-full border border-white/10 bg-black/25 px-3 py-1 text-xs font-semibold text-white/70">
                {phaseMeta[phase].shortLabel}
              </span>
            </div>
          </div>
          <div className="mt-5">
            <p className="text-xs uppercase tracking-[0.24em] text-white/50">{service.category || "AI Service"}</p>
            <h3 className="mt-2 text-2xl font-black text-white">{service.name}</h3>
            <p className="mt-2 min-h-12 text-sm leading-6 text-slate-200">{service.tagline || service.description}</p>
          </div>
        </div>

        <p className="text-sm leading-6 text-slate-300">{service.description}</p>

        <div className="grid grid-cols-3 gap-2">
          <MiniFact icon={CreditCard} label="Precio" value={service.billing?.displayPrice || "Demo"} />
          <MiniFact icon={Zap} label="Tiempo" value={`${service.estimatedTimeSec || 30}s`} />
          <MiniFact icon={isExternal ? ExternalLink : LayoutGrid} label="Modo" value={isExternal ? "Externa" : "Interna"} />
        </div>

        <div className="flex flex-wrap gap-2">
          {(service.tags || []).slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-300">
              {tag}
            </span>
          ))}
          {hiddenTags > 0 && (
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-slate-400">+{hiddenTags}</span>
          )}
        </div>

        <div className="space-y-2">
          {(service.highlights || []).slice(0, 3).map((highlight) => (
            <div key={highlight} className="flex items-start gap-2 text-sm text-slate-300">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-200" />
              <span>{highlight}</span>
            </div>
          ))}
        </div>

        <div className="mt-auto rounded-2xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Valor comercial</p>
          <p className="mt-2 text-sm text-slate-200">{service.businessValue || "Demo lista para discovery comercial."}</p>
        </div>

        {disclaimers.length ? (
          <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 p-4">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-amber-100">
              <AlertTriangle className="h-3.5 w-3.5" />
              Disclaimer
            </div>
            <p className="mt-2 text-xs leading-5 text-amber-50/90">{disclaimers[0]}</p>
          </div>
        ) : null}

        {isExternal ? (
          <Button asChild className="h-12 w-full rounded-2xl bg-white text-slate-950 hover:bg-cyan-100">
            <Link to={`${service.id}`}>
              {cta}
              <ExternalLink className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        ) : (
          <Button asChild className="h-12 w-full rounded-2xl bg-white text-slate-950 hover:bg-cyan-100">
            <Link to={`${service.id}`}>
              {cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function MiniFact({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
      <div className="flex items-center gap-1.5 text-slate-400">
        <Icon className="h-3.5 w-3.5" />
        <span className="text-[0.65rem] uppercase tracking-[0.16em]">{label}</span>
      </div>
      <p className="mt-1 truncate text-sm font-bold text-white">{value}</p>
    </div>
  );
}

function EmptyCatalog({ onReset }: { onReset: () => void }) {
  return (
    <div className="rounded-[2rem] border border-white/10 bg-white/[0.045] p-10 text-center">
      <FlaskConical className="mx-auto h-14 w-14 text-cyan-200/70" />
      <h3 className="mt-5 text-2xl font-black">No hay servicios con esa combinación</h3>
      <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-400">
        Ajusta la búsqueda o limpia filtros. El catálogo soporta muchas categorías y fases, pero evita mostrar tarjetas irrelevantes.
      </p>
      <Button className="mt-6 rounded-full bg-white px-6 text-slate-950 hover:bg-cyan-100" onClick={onReset}>
        Ver todo el catálogo
      </Button>
    </div>
  );
}

function uniqueSorted(values: Array<string | undefined>) {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort((a, b) => a.localeCompare(b));
}

function serviceDisclaimers(service: AiService) {
  const text = [
    service.id,
    service.name,
    service.category,
    service.description,
    ...(service.tags || []),
  ].join(" ").toLowerCase();
  const disclaimers = new Set<string>(service.disclaimers || []);
  if (/(legal|contrato|contract|derecho|petition|traffic|multa|compliance|medical|medgemma|salud|wellness|screening|tenant|arriendo|tax|invoice|audit|finance)/i.test(text)) {
    disclaimers.add("Orientación informativa. No constituye asesoría profesional definitiva; valida con un profesional o autoridad competente antes de tomar decisiones sensibles.");
  }
  if (/(medical|medgemma|salud|symptom|medication)/i.test(text)) {
    disclaimers.add("No diagnostica ni prescribe. En emergencias o síntomas graves contacta servicios médicos de inmediato.");
  }
  if (/(legal|derecho|traffic|multa|contract|petition)/i.test(text)) {
    disclaimers.add("Los borradores legales deben revisarse según jurisdicción, fechas, evidencia y norma vigente antes de enviarse.");
  }
  return Array.from(disclaimers);
}

function getServicePhase(service: AiService): CatalogPhaseId {
  const explicitPhase = service.phase ?? service.roadmapPhase ?? service.catalogPhase;
  const normalized = String(explicitPhase || "").toLowerCase();
  if (normalized.includes("4")) return "phase-4";
  if (normalized.includes("3")) return "phase-3";
  if (normalized.includes("2")) return "phase-2";
  if (normalized.includes("1")) return "phase-1";

  const text = [service.name, service.category, service.description, service.provider, ...(service.tags || []), ...(service.useCases || [])]
    .join(" ")
    .toLowerCase();

  if (text.match(/medical|salud|instagram|whatsapp automation|scraping|scraper|lead data|compliance|legal/)) return "phase-3";
  if (text.match(/audio|imagen|image|ocr|pdf|docx|xlsx|csv|gis|geodatabase|transcrip|archivo|upload/)) return "phase-2";
  if (service.launchMode === "external") return "phase-2";
  return "phase-1";
}

function sortServices(a: AiService, b: AiService, sortMode: SortMode) {
  if (sortMode === "name") return a.name.localeCompare(b.name);
  if (sortMode === "price-asc") return getPriceCents(a) - getPriceCents(b) || getSortOrder(a) - getSortOrder(b);
  if (sortMode === "time-asc") return (a.estimatedTimeSec || 999) - (b.estimatedTimeSec || 999) || getSortOrder(a) - getSortOrder(b);
  return getSortOrder(a) - getSortOrder(b) || a.name.localeCompare(b.name);
}

function getSortOrder(service: AiService) {
  return service.sortOrder ?? 999;
}

function getPriceCents(service: AiService) {
  if (typeof service.billing?.unitAmount === "number") return service.billing.unitAmount;
  const parsed = Number((service.billing?.displayPrice || "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed * 100 : 0;
}
